import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet/Sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/AlertDialog/AlertDialog";
import { Separator } from "@/components/ui/Separator/Separator";
import { VStack } from "@/components/layout/VStack";
import { HStack } from "@/components/layout/HStack";
import type { ApiErrorLog } from "@shared/types/models";

const ViewErrorLogSheet = ({
  log,
  open,
  onOpenChange,
  deleting,
  onDelete,
}: {
  log: ApiErrorLog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deleting: boolean;
  onDelete: () => Promise<void>;
}) => {
  const when = new Date(log.createdAt).toLocaleString();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>Error details</SheetTitle>
          <SheetDescription>
            Captured {when}. Logs are retained for 14 days.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <HStack gap="0.5rem" wrap>
            <VStack gap="2px" style={{ flex: 1, minWidth: "120px" }}>
              <Label>Status</Label>
              <Input value={log.status?.toString() ?? "—"} disabled readOnly />
            </VStack>
            <VStack gap="2px" style={{ flex: 1, minWidth: "120px" }}>
              <Label>Method</Label>
              <Input value={log.method ?? "—"} disabled readOnly />
            </VStack>
          </HStack>
          <VStack gap="2px">
            <Label>Path</Label>
            <Input value={log.path ?? "—"} disabled readOnly />
          </VStack>
          <VStack gap="2px">
            <Label>User</Label>
            <Input
              value={
                log.user
                  ? `${log.user.name} <${log.user.email}>`
                  : "—"
              }
              disabled
              readOnly
            />
          </VStack>
          <VStack gap="2px">
            <Label>User agent</Label>
            <Input value={log.userAgent ?? "—"} disabled readOnly />
          </VStack>
          <VStack gap="2px">
            <Label>Message</Label>
            <pre
              style={{
                margin: 0,
                padding: "var(--space-xs)",
                background: "var(--muted)",
                color: "var(--foreground)",
                border: "1px solid var(--secondary-border)",
                borderRadius: "var(--radius)",
                fontSize: "var(--text-sm)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {log.message}
            </pre>
          </VStack>
          {log.stack && (
            <VStack gap="2px">
              <Label>Stack</Label>
              <pre
                style={{
                  margin: 0,
                  padding: "var(--space-xs)",
                  background: "var(--muted)",
                  color: "var(--foreground)",
                  border: "1px solid var(--secondary-border)",
                  borderRadius: "var(--radius)",
                  fontSize: "var(--text-xs)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: "320px",
                  overflow: "auto",
                }}
              >
                {log.stack}
              </pre>
            </VStack>
          )}
          <Separator />
          <VStack gap="4px">
            <Label>Danger zone</Label>
            <p
              style={{
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              Deleting this log entry is permanent. All entries are
              automatically deleted after 14 days.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  style={{ width: "100%" }}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete log"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this error log?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the entry. This action cannot be
                    undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <HStack gap="0.5rem" justify="flex-end">
                    <AlertDialogCancel asChild>
                      <Button variant="outline">Cancel</Button>
                    </AlertDialogCancel>
                    <AlertDialogAction asChild>
                      <Button
                        variant="destructive"
                        onClick={onDelete}
                        disabled={deleting}
                      >
                        Delete
                      </Button>
                    </AlertDialogAction>
                  </HStack>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </VStack>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline" style={{ width: "100%" }}>
              Close
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export { ViewErrorLogSheet };
